import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import {
  FloppyDisk,
  Image as ImageIcon,
  ArrowLeft
} from '@phosphor-icons/react';
import api from '../../../utils/api';
import { useBusiness } from '../../../context/BusinessContext';
import useThemeStyles from '../../../theme/useThemeStyles';

const MAX_INVOICE_LOGO_SIZE_BYTES = 2 * 1024 * 1024;
const VALID_INVOICE_LOGO_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

const getContrastTextColor = (backgroundColor, lightText = '#ffffff', darkText = '#111827') => {
  if (!HEX_COLOR_REGEX.test(backgroundColor || '')) return lightText;
  const r = parseInt(backgroundColor.slice(1, 3), 16) / 255;
  const g = parseInt(backgroundColor.slice(3, 5), 16) / 255;
  const b = parseInt(backgroundColor.slice(5, 7), 16) / 255;
  const linear = [r, g, b].map((channel) =>
    channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  );
  const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithBlack = (luminance + 0.05) / 0.05;
  return contrastWithWhite >= contrastWithBlack ? lightText : darkText;
};

function InvoiceSettings() {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    invoiceNumberPrefix: 'INV',
    footerText: 'Thank you for your business!',
    logo: '',
    billingCredentials: '',
    primaryColor: '#000000'
  });
  const previewTextColor = getContrastTextColor(formData.primaryColor);
  const fixedAlertSx = {
    position: 'fixed',
    top: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100% - 32px)',
    maxWidth: 720,
    zIndex: (theme) => theme.zIndex.snackbar
  };

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const businessId = selectedBusiness?.business?._id;
      if (!businessId) {
        setLoading(false);
        return;
      }

      const response = await api.get(`/api/invoices/settings/${businessId}`);
      const settings = response.data;

      setFormData({
        invoiceNumberPrefix: settings.invoiceSettings?.invoiceNumberPrefix || 'INV',
        footerText: settings.invoiceSettings?.footerText || 'Thank you for your business!',
        logo: settings.invoiceSettings?.logo || '',
        billingCredentials: settings.invoiceSettings?.billingCredentials || '',
        primaryColor: settings.invoiceSettings?.primaryColor || '#000000'
      });
    } catch (error) {
      setError('Failed to load invoice settings');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness?.business?._id]);

  useEffect(() => {
    const businessId = selectedBusiness?.business?._id;
    if (businessId) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [selectedBusiness, fetchSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const businessId = selectedBusiness?.business?._id;
      if (!businessId) {
        setError('Business not found');
        return;
      }

      await api.put(`/api/invoices/settings/${businessId}`, formData);
      setSuccess('Invoice settings saved successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save invoice settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    setSuccess('');

    if (!VALID_INVOICE_LOGO_TYPES.includes(file.type)) {
      setError('Please upload a PNG or JPEG logo.');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_INVOICE_LOGO_SIZE_BYTES) {
      setError('Invoice logo size must be 2MB or smaller.');
      e.target.value = '';
      return;
    }

    try {
      setSaving(true);

      // Create form data for upload
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const businessId = selectedBusiness?.business?._id;
      if (!businessId) {
        setError('Business not found');
        return;
      }

      const uploadResponse = await api.post(`/api/invoices/settings/${businessId}/logo`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const updatedFormData = { ...formData, logo: uploadResponse.data.imageUrl };
      await api.put(`/api/invoices/settings/${businessId}`, updatedFormData);
      setFormData(updatedFormData);
      setSuccess('Invoice logo uploaded and saved successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to upload logo');
    } finally {
      setSaving(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/supplier/invoices')} sx={{ mr: 1 }}>
          <ArrowLeft size={24} />
        </IconButton>
        <Box>
          <Typography component="h2" className="pagename" sx={styles.text.pagename}>
            Invoice Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customise how your invoices appear to couples
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={fixedAlertSx} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={fixedAlertSx} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Invoice Customisation
              </Typography>

              <Grid container spacing={3}>
                {/* Logo Upload */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Invoice Logo
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {formData.logo && (
                      <Box
                        component="img"
                        src={formData.logo}
                        alt="Invoice Logo"
                        sx={{
                          maxWidth: 120,
                          maxHeight: 120,
                          objectFit: 'contain',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1
                        }}
                      />
                    )}
                    <Box>
                      <input
                        accept="image/png,image/jpeg"
                        style={{ display: 'none' }}
                        id="logo-upload"
                        type="file"
                        onChange={handleLogoUpload}
                      />
                      <label htmlFor="logo-upload">
                        <Button
                          component="span"
                          variant="outlined"
                          startIcon={<ImageIcon size={18} />}
                          size="small"
                        >
                          {formData.logo ? 'Change Logo' : 'Upload Logo'}
                        </Button>
                      </label>
                      {formData.logo && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setFormData({ ...formData, logo: '' })}
                          sx={{ ml: 1 }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Recommended: up to 120×120px. PNG or JPEG only. Max file size: 2MB. Will appear at the top of your invoices.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Invoice Prefix */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Invoice Number Prefix"
                    value={formData.invoiceNumberPrefix}
                    onChange={(e) => setFormData({ ...formData, invoiceNumberPrefix: e.target.value.toUpperCase() })}
                    helperText="Prefix for invoice numbers (e.g., INV, BILL)"
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>

                {/* Primary Color */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Invoice Colour"
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ 
                        style: { height: 56, cursor: 'pointer' }
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Colour Code"
                      value={formData.primaryColor}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
                          setFormData({ ...formData, primaryColor: value || '#000000' });
                        }
                      }}
                      placeholder="#000000"
                      helperText="Used for table headers and borders"
                    />
                  </Box>
                </Grid>

                {/* Header Text */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Header Text"
                    value={formData.billingCredentials}
                    onChange={(e) => setFormData({ ...formData, billingCredentials: e.target.value })}
                    helperText="Contact information displayed below your business name on invoices (e.g., email, phone, address)"
                    inputProps={{ maxLength: 500 }}
                    placeholder="e.g., hello@example.com | +44 123 456 7890 | 123 Business St, City, UK"
                  />
                </Grid>

                {/* Footer Text */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Footer Text"
                    value={formData.footerText}
                    onChange={(e) => setFormData({ ...formData, footerText: e.target.value })}
                    helperText="Custom message displayed at the bottom of invoices. Include your payment information here (e.g., bank account details, payment terms, etc.)"
                    inputProps={{ maxLength: 500 }}
                  />
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="caption">
                      <strong>Tip:</strong> Include your payment information in the footer text (e.g., bank account details, payment terms, payment methods accepted, etc.) so couples know how to pay you.
                    </Typography>
                  </Alert>
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<FloppyDisk size={18} />}
                      onClick={handleSubmit}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Settings'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Preview Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Preview
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1, minHeight: 200 }}>
                  {formData.logo ? (
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={formData.logo}
                        alt="Logo Preview"
                        style={{ maxWidth: 120, maxHeight: 120, width: 'auto', height: 'auto', objectFit: 'contain' }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {selectedBusiness?.business?.name || 'Your Business Name'}
                    </Typography>
                  )}
                  {formData.billingCredentials && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {formData.billingCredentials}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 2, borderColor: formData.primaryColor }} />
                  <Box sx={{ 
                    bgcolor: formData.primaryColor, 
                    color: previewTextColor,
                    p: 1, 
                    borderRadius: 0.5, 
                    mb: 2,
                    fontSize: '0.75rem'
                  }}>
                    Table Header Preview
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {formData.footerText}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default InvoiceSettings;
